import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import ControlPanelLayout from "./ControlPanelLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Copy, RefreshCw, Trash2, ShieldCheck, ShieldAlert, Loader2, KeyRound, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API_BASE = "https://wdshnonjtrfhreccaqul.supabase.co/functions/v1/v1-api";

interface App {
  id: string;
  name: string;
  description: string | null;
  verified_at: string | null;
  created_at: string;
}

interface Key {
  id: string;
  app_id: string;
  key: string;
  is_active: boolean;
  created_at: string;
}

function genKey() {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  const hex = Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `mh_live_${hex}`;
}

export default function CPApps() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [keys, setKeys] = useState<Record<string, Key | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data: appsData } = await supabase
      .from("apps")
      .select("id, name, description, verified_at, created_at")
      .eq("owner_id", user.id)
      .order("created_at", { ascending: false });
    setApps(appsData || []);

    if (appsData?.length) {
      const { data: keysData } = await supabase
        .from("api_keys")
        .select("*")
        .in("app_id", appsData.map((a) => a.id))
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      const map: Record<string, Key | undefined> = {};
      (keysData || []).forEach((k) => {
        if (!map[k.app_id]) map[k.app_id] = k as Key;
      });
      setKeys(map);
    } else {
      setKeys({});
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [user]);

  const createApp = async () => {
    if (!name.trim() || !user) return;
    const { data, error } = await supabase
      .from("apps")
      .insert({ owner_id: user.id, name: name.trim(), description: desc.trim() || null })
      .select()
      .single();
    if (error) return toast.error(error.message);
    await supabase.from("api_keys").insert({ app_id: data.id, key: genKey(), name: "Default" });
    toast.success("App created");
    setCreateOpen(false);
    setName("");
    setDesc("");
    load();
  };

  const regenerate = async (appId: string) => {
    if (!confirm("Revoke the existing key and generate a new one?")) return;
    await supabase.from("api_keys").update({ is_active: false }).eq("app_id", appId);
    await supabase.from("api_keys").insert({ app_id: appId, key: genKey(), name: "Rotated" });
    await supabase.from("apps").update({ verified_at: null }).eq("id", appId);
    toast.success("New key generated");
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this app and its key?")) return;
    const { error } = await supabase.from("apps").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  const verify = async (appId: string, key: string) => {
    setVerifying(appId);
    try {
      const res = await fetch(`${API_BASE}/meetings`, {
        headers: { Authorization: `Bearer ${key}` },
      });
      if (!res.ok) {
        toast.error("Key invalid - verification failed");
        await supabase.from("apps").update({ verified_at: null }).eq("id", appId);
      } else {
        await supabase
          .from("apps")
          .update({ verified_at: new Date().toISOString() })
          .eq("id", appId);
        toast.success("Key verified");
      }
      load();
    } catch (e) {
      toast.error("Network error during verify");
    } finally {
      setVerifying(null);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const verifiedCount = apps.filter((app) => app.verified_at).length;

  return (
    <ControlPanelLayout>
      <div className="space-y-6">
        <section className="rounded-3xl border border-border/60 bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--primary)/0.08))] p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Apps and keys</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">Manage the credentials your integrations rely on.</h2>
              <p className="mt-3 text-muted-foreground">
                Create an app, copy the current key, verify it against MeetHub, and rotate credentials safely when needed.
              </p>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" /> New app
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create app</DialogTitle>
                  <DialogDescription>An API key will be generated automatically.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>App name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My SaaS" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createApp} disabled={!name.trim()}>
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </section>

        <div className="grid gap-4 md:grid-cols-3">
          <SummaryCard label="Total apps" value={loading ? "..." : String(apps.length)} helper="Registered integration workspaces" />
          <SummaryCard label="Verified" value={loading ? "..." : String(verifiedCount)} helper="Ready for production traffic" />
          <SummaryCard label="Active keys" value={loading ? "..." : String(Object.values(keys).filter(Boolean).length)} helper="Current credentials on hand" />
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : apps.length === 0 ? (
          <Card className="border-border/60 shadow-sm">
            <CardContent className="py-14 text-center">
              <KeyRound className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 text-lg font-medium">No apps yet</p>
              <p className="mt-2 text-sm text-muted-foreground">Create your first app to generate an API key and begin verification.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {apps.map((app) => {
              const k = keys[app.id];
              return (
                <Card key={app.id} className="border-border/60 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          {app.name}
                          {app.verified_at ? (
                            <Badge variant="default" className="gap-1">
                              <ShieldCheck className="h-3 w-3" /> Verified
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <ShieldAlert className="h-3 w-3" /> Needs verification
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-2 text-sm">
                          {app.description || "No description added yet."}
                        </CardDescription>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => remove(app.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4 sm:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">App id</p>
                        <p className="mt-1 break-all text-sm font-medium">{app.id}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Created</p>
                        <p className="mt-1 text-sm font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Current API key</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={k?.key || "- no active key -"}
                          className="font-mono text-xs"
                        />
                        {k && (
                          <Button variant="outline" size="icon" onClick={() => copy(k.key)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {k && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={verifying === app.id}
                          onClick={() => verify(app.id, k.key)}
                        >
                          {verifying === app.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <ShieldCheck className="h-3 w-3 mr-1" />
                          )}
                          Verify key
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => regenerate(app.id)}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Rotate key
                      </Button>
                    </div>

                    <div className="rounded-2xl border border-dashed border-border bg-background/70 p-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Best practice
                      </div>
                      <p className="mt-2">
                        Verify every fresh key before sharing it with a production app so you catch permission or environment issues early.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </ControlPanelLayout>
  );
}

function SummaryCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardContent className="p-5">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
      </CardContent>
    </Card>
  );
}
